import { useTranslation } from "react-i18next";
import VerticalLanding, { type VerticalCopy } from "./VerticalLanding";

type Lang = "de" | "en";
const useLang = (): Lang => {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith("de") ? "de" : "en";
};

const copy: Record<Lang, VerticalCopy> = {
  de: {
    eyebrow: "FÜR AGENTUR-INHABER & MANAGING PARTNERS",
    h1a: "Ihr Delivery-Modell —",
    h1b: "läuft als autonomes ",
    h1hl: "System.",
    h1c: "White-Label. In 90 Tagen.",
    lead: "Wir bauen die Loops, die Ihre Agentur bisher manuell fährt — AI-Visibility, Reporting, Content-Ops. In Ihrer Cloud, mit Ihrem Branding. Ein Festpreis, kein Retainer.",
    cta: "Erstgespräch anfragen",
    ctaNote: "// 30 Min. — kein Verkaufsgespräch",
    stackLine: "// Anbindung: HubSpot · Notion · Slack · GA4 · Ihre Datenquellen",
    stats: [
      { num: "60–70 %", label: "der Agentur-Kapazität geht in wiederholbare Delivery — nicht in Strategie.", src: "SoDA / Agency Benchmark Reports" },
      { num: "6–9 Mo.", label: "durchschnittliche Kunden-Lebensdauer bei rein manueller Delivery.", src: "SaaS/Agency Retention Benchmarks" },
      { num: "3×", label: "höhere Marge, wenn Delivery-Ops als System statt als Stunden verkauft werden.", src: "Agency Ops Studies" },
    ],
    loop: { title: "system/loop — client_runner", running: "RUNNING" },
    stages: [
      { icon: "◉", name: "detect", sub: "Signal erkannt" },
      { icon: "⚡", name: "trigger", sub: "Job gestartet" },
      { icon: "✎", name: "draft", sub: "Deliverable entworfen" },
      { icon: "✓", name: "approve", sub: "Freigabe Account", gate: true },
      { icon: "▶", name: "execute", sub: "Ausliefern + Reporten" },
      { icon: "◫", name: "measure", sub: "Wirkung messen" },
    ],
    gateTag: "HITL",
    log: {
      detect: "Signal: Kunde X — neue Prompt-Rankings in Perplexity",
      trigger: "Job #C-1841 in Warteschlange — Account: Kunde X",
      draft: "Deliverable generiert: Visibility-Report + 3 Content-Empfehlungen",
      approve: "Account-Manager freigegeben — Versand vorbereitet",
      execute: "Report an Kunde X versandt · Slack-Update gepostet",
      measure: "Impact getrackt · CTR +12% ggü. Vorwoche",
    },
    problem: {
      eyebrow: "// DIAGNOSE",
      h2a: "Das Problem ist nicht Talent.",
      h2b: "Das Problem ist Wiederholung.",
      lead: "Ihre besten Leute verbringen den Tag mit Reports, die letzte Woche schon so aussahen. Skalieren heißt einstellen. Marge sinkt mit jedem neuen Kunden.",
      cards: [
        { tag: "// STUNDEN-FALLE", title: "Sie verkaufen Zeit, nicht Ergebnis.", body: "Solange Delivery an Menschen hängt, ist Wachstum linear und Marge gedeckelt. Jeder neue Kunde braucht neue Kapazität." },
        { tag: "// TOOL-FALLE", title: "10 Tools, kein System.", body: "Zapier, Notion, Sheets, ChatGPT — alles verbunden, nichts autonom. Die Naht zwischen den Tools ist immer ein Mensch." },
        { tag: "// RETAINER-FALLE", title: "Kunden zahlen für Reports, nicht für Wirkung.", body: "Ohne messbare Loops bleibt der Retainer angreifbar. Der erste Preiskampf verliert den Kunden." },
      ],
      note1: "Wir bauen die Loops, die Ihre Agentur wiederholbar macht — als System, nicht als Playbook. Ihre Marke, Ihre Cloud, Ihr Eigentum.",
      note2: "Am Ende der 90 Tage läuft es. Oder Sie zahlen nicht.",
    },
    mechanism: {
      eyebrow: "// SO FUNKTIONIERT ES",
      h2: "Sechs Stufen. Eine Schleife. White-Label.",
      lead: "Ein Delivery-Prozess Ihrer Agentur wird zerlegt, jede Stufe autonom ausgeführt, eine Stufe bleibt beim Account-Team: die Freigabe.",
      rows: [
        { name: "detect", desc: "System überwacht Kunden-Signale (Rankings, Traffic, AI-Sichtbarkeit, Erwähnungen) kontinuierlich." },
        { name: "trigger", desc: "Für jedes Signal wird ein Job pro Kunde angelegt, priorisiert, geplant." },
        { name: "draft", desc: "Deliverable (Report, Empfehlung, Content-Draft) wird generiert — mit Ihrem Ton, Ihren Templates." },
        { name: "approve", desc: "Account-Manager gibt in einem Klick frei. Diese Stufe bleibt beim Menschen.", gate: true },
        { name: "execute", desc: "Automatischer Versand an Kunde, Slack-Update ans Team, CRM-Log." },
        { name: "measure", desc: "Wirkung pro Deliverable getrackt: Engagement, Impact, Kundenreaktion." },
      ],
    },
    useCases: {
      eyebrow: "// EINSATZFELDER",
      h2: "Was wir für Agenturen bauen.",
      lead: "Vier Loop-Familien, die in fast jeder Agentur dieselben Stunden fressen.",
      items: [
        { tag: "// AI-VISIBILITY", title: "GEO/AI-Search als Service.", body: "Monitoring und Optimierung der Kunden-Sichtbarkeit in ChatGPT, Perplexity, Google AI Overviews. Als eigenes System oder als White-Label auf webblitz.ai.", link: { label: "webblitz.ai ansehen", href: "https://webblitz.ai" }, external: true },
        { tag: "// REPORTING-OPS", title: "Kunden-Reports, autonom.", body: "Wöchentliche/monatliche Reports werden aus GA4, HubSpot, Search Console generiert. Account gibt frei, System versendet — in Ihrem Branding." },
        { tag: "// CONTENT-OPS", title: "Content-Pipeline mit Approve-Gate.", body: "Brief-generation, Draft, Review, Publishing — alles in einer Loop. Autoren werden zum Editor, nicht zum Producer." },
        { tag: "// LEAD-ENRICHMENT", title: "Inbound-Leads angereichert und priorisiert.", body: "Neue Leads werden aus offenen Quellen angereichert, gescored und Ihrem Sales-Team mit Kontext übergeben." },
      ],
    },
    outcome: {
      eyebrow: "// ERGEBNIS",
      h2: "Was Sie am Tag 91 haben.",
      cards: [
        { ic: "MARGE", title: "Delivery skaliert ohne neue Köpfe.", body: "Der nächste Kunde braucht keinen weiteren Junior. Marge wächst mit dem Umsatz, nicht dagegen." },
        { ic: "RETAIN", title: "Kunden bleiben, weil sie Wirkung sehen.", body: "Autonome Loops liefern regelmäßiger und messbarer als Menschen. Der Retainer wird verteidigungsfähig." },
        { ic: "OWN", title: "Das System gehört Ihnen.", body: "White-Label, in Ihrer Cloud, mit Ihrem Branding. Kein Wiederverkauf, keine Abhängigkeit — Ihr Asset auf der Bilanz." },
      ],
    },
    mid: {
      eyebrow: "// NÄCHSTER SCHRITT",
      h3: "Welcher Ihrer Delivery-Prozesse ist reif für die Schleife?",
      lead: "30 Min. Erstgespräch. Wir schauen uns einen konkreten Delivery-Flow an und sagen ehrlich, ob und wo Automatisierung sich rechnet.",
      cta: "Erstgespräch anfragen",
    },
    phases: {
      eyebrow: "// PROGRAMM",
      h2: "90 Tage. Drei Phasen. Ein Festpreis. Am Ende läuft es.",
      lead: "Keine Roadmap, kein PoC, kein ‚wir sehen dann weiter'. Ein klarer Bauplan mit definiertem Ende.",
      p: [
        { days: "TAG 1–30", title: "Delivery-Cut.", bullets: ["Einen Delivery-Prozess auswählen und schneiden.", "Datenquellen, Tools, Templates klären.", "Approve-Gate mit Ihrem Account-Team definieren."] },
        { days: "TAG 31–60", title: "Build.", bullets: ["Loop implementieren, in Ihrer Cloud, White-Label.", "Templates & Ton aus Ihrer bisherigen Delivery ableiten.", "Wöchentliche Demos — kein Blackbox-Build."] },
        { days: "TAG 61–90", title: "Live.", bullets: ["Schrittweise Übergabe: Schatten-Betrieb, dann Voll-Betrieb.", "Runbook, Monitoring, Alerting.", "Ihr Team betreibt es. Wir bleiben optional als Service."] },
      ],
    },
    buying: {
      eyebrow: "// SO KAUFEN SIE DAS",
      h2: "Kein White-Label-Abo. Kein Umsatz-Split. Keine Mindestlaufzeit.",
      p1: "Ein Festpreis für den 90-Tage-Build. Das System läuft in Ihrer Cloud und gehört Ihnen — Betrieb und Weiterentwicklung optional als monatlicher Service.",
      p2: "Sie verkaufen es weiter, wie Sie wollen. Wir sind der Bauhof, nicht der Vermieter.",
      pills: ["Festpreis", "In Ihrer Cloud", "White-Label", "Ihr Eigentum"],
    },
    guarantee: {
      eyebrow: "// GARANTIE",
      h2: "Am Tag 91 läuft es. Oder Sie zahlen nicht.",
      body: "Wenn das System nach 90 Tagen nicht produktiv im Betrieb ist, bekommen Sie den Festpreis zurück. Ohne Diskussion.",
    },
    final: {
      brand: "// RAW RESPONSE LABS × IHRE AGENTUR",
      h2a: "Ein Delivery-Prozess.",
      h2b: "Ein ",
      h2hl: "Festpreis.",
      h2c: " 90 Tage.",
      lead: "30 Min. Erstgespräch. Wir schauen uns einen konkreten Delivery-Flow an. Am Ende wissen Sie, ob und wie sich das für Sie rechnet.",
      cta: "Erstgespräch anfragen",
      ctaNote: "// Kein Verkaufsgespräch. Kein Pitch. Ein Blick auf Ihre Delivery.",
      sticky: "Erstgespräch",
    },
  },
  en: {
    eyebrow: "FOR AGENCY OWNERS & MANAGING PARTNERS",
    h1a: "Your delivery model —",
    h1b: "run by an autonomous ",
    h1hl: "system.",
    h1c: "White-label. In 90 days.",
    lead: "We build the loops your agency runs by hand today — AI-visibility, reporting, content ops. In your cloud, with your branding. One fixed price, no retainer.",
    cta: "Book intro call",
    ctaNote: "// 30 min — no sales pitch",
    stackLine: "// Integrations: HubSpot · Notion · Slack · GA4 · your data sources",
    stats: [
      { num: "60–70%", label: "of agency capacity goes into repeatable delivery — not strategy.", src: "SoDA / Agency Benchmark Reports" },
      { num: "6–9 mo", label: "average client lifetime with fully manual delivery.", src: "SaaS/Agency Retention Benchmarks" },
      { num: "3×", label: "higher margin when delivery ops are sold as a system, not hours.", src: "Agency Ops Studies" },
    ],
    loop: { title: "system/loop — client_runner", running: "RUNNING" },
    stages: [
      { icon: "◉", name: "detect", sub: "Signal detected" },
      { icon: "⚡", name: "trigger", sub: "Job queued" },
      { icon: "✎", name: "draft", sub: "Deliverable drafted" },
      { icon: "✓", name: "approve", sub: "Account approves", gate: true },
      { icon: "▶", name: "execute", sub: "Deliver + report" },
      { icon: "◫", name: "measure", sub: "Track impact" },
    ],
    gateTag: "HITL",
    log: {
      detect: "Signal: Client X — new prompt rankings on Perplexity",
      trigger: "Job #C-1841 queued — account: Client X",
      draft: "Deliverable generated: visibility report + 3 content recs",
      approve: "Account manager approved — send prepared",
      execute: "Report sent to Client X · Slack update posted",
      measure: "Impact tracked · CTR +12% vs prior week",
    },
    problem: {
      eyebrow: "// DIAGNOSIS",
      h2a: "The problem isn't talent.",
      h2b: "The problem is repetition.",
      lead: "Your best people spend the day on reports that looked the same last week. Scaling means hiring. Margin drops with every new client.",
      cards: [
        { tag: "// HOURS TRAP", title: "You sell time, not outcome.", body: "As long as delivery depends on people, growth is linear and margin is capped. Every new client demands new capacity." },
        { tag: "// TOOL TRAP", title: "10 tools, no system.", body: "Zapier, Notion, Sheets, ChatGPT — all connected, nothing autonomous. The seam between the tools is always a human." },
        { tag: "// RETAINER TRAP", title: "Clients pay for reports, not impact.", body: "Without measurable loops, the retainer stays exposed. The first price fight loses the client." },
      ],
      note1: "We build the loops that make your delivery repeatable — as a system, not a playbook. Your brand, your cloud, your asset.",
      note2: "At the end of 90 days it runs. Or you don't pay.",
    },
    mechanism: {
      eyebrow: "// HOW IT WORKS",
      h2: "Six stages. One loop. White-label.",
      lead: "One of your agency's delivery processes gets decomposed, each stage runs autonomously, one stage stays with the account team: the approval.",
      rows: [
        { name: "detect", desc: "System continuously monitors client signals (rankings, traffic, AI visibility, mentions)." },
        { name: "trigger", desc: "For each signal a per-client job is created, prioritised, scheduled." },
        { name: "draft", desc: "Deliverable (report, recommendation, content draft) is generated — in your voice, your templates." },
        { name: "approve", desc: "Account manager approves in one click. This stage stays with the human.", gate: true },
        { name: "execute", desc: "Automatic send to client, Slack update to the team, CRM log." },
        { name: "measure", desc: "Impact per deliverable tracked: engagement, effect, client response." },
      ],
    },
    useCases: {
      eyebrow: "// WHERE IT FITS",
      h2: "What we build for agencies.",
      lead: "Four loop families that eat the same hours in almost every agency.",
      items: [
        { tag: "// AI-VISIBILITY", title: "GEO/AI-search as a service.", body: "Monitor and optimise client visibility across ChatGPT, Perplexity, Google AI Overviews. As your own system — or white-labelled on top of webblitz.ai.", link: { label: "See webblitz.ai", href: "https://webblitz.ai" }, external: true },
        { tag: "// REPORTING OPS", title: "Client reports, autonomous.", body: "Weekly/monthly reports generated from GA4, HubSpot, Search Console. Account approves, system sends — in your branding." },
        { tag: "// CONTENT OPS", title: "Content pipeline with an approve gate.", body: "Brief generation, draft, review, publishing — all in one loop. Writers become editors, not producers." },
        { tag: "// LEAD ENRICHMENT", title: "Inbound leads enriched and prioritised.", body: "New leads enriched from open sources, scored, and handed to your sales team with context." },
      ],
    },
    outcome: {
      eyebrow: "// OUTCOME",
      h2: "What you have on day 91.",
      cards: [
        { ic: "MARGIN", title: "Delivery scales without new headcount.", body: "The next client doesn't need another junior. Margin grows with revenue, not against it." },
        { ic: "RETAIN", title: "Clients stay because they see impact.", body: "Autonomous loops deliver more consistently and measurably than people. The retainer becomes defensible." },
        { ic: "OWN", title: "The system belongs to you.", body: "White-label, in your cloud, in your branding. No reseller, no dependency — your asset on the balance sheet." },
      ],
    },
    mid: {
      eyebrow: "// NEXT STEP",
      h3: "Which of your delivery processes is ready for the loop?",
      lead: "30 min intro call. We look at one concrete delivery flow and tell you honestly whether and where automation pays off.",
      cta: "Book intro call",
    },
    phases: {
      eyebrow: "// PROGRAM",
      h2: "90 days. Three phases. One fixed price. At the end it runs.",
      lead: "No roadmap, no PoC, no ‘let's see'. A clear blueprint with a defined end.",
      p: [
        { days: "DAY 1–30", title: "Delivery cut.", bullets: ["Pick one delivery process and cut it.", "Clarify data sources, tools, templates.", "Define the approve gate with your account team."] },
        { days: "DAY 31–60", title: "Build.", bullets: ["Implement the loop, in your cloud, white-label.", "Derive templates & voice from your existing delivery.", "Weekly demos — no black-box build."] },
        { days: "DAY 61–90", title: "Live.", bullets: ["Gradual handover: shadow mode, then full ops.", "Runbook, monitoring, alerting.", "Your team runs it. We stay optional as a service."] },
      ],
    },
    buying: {
      eyebrow: "// HOW YOU BUY THIS",
      h2: "No white-label subscription. No revenue split. No minimum term.",
      p1: "A fixed price for the 90-day build. The system runs in your cloud and belongs to you — operation and further development optional as a monthly service.",
      p2: "You resell it however you like. We're the workshop, not the landlord.",
      pills: ["Fixed price", "In your cloud", "White-label", "You own it"],
    },
    guarantee: {
      eyebrow: "// GUARANTEE",
      h2: "On day 91 it runs. Or you don't pay.",
      body: "If the system isn't in productive operation after 90 days, you get the fixed price back. No discussion.",
    },
    final: {
      brand: "// RAW RESPONSE LABS × YOUR AGENCY",
      h2a: "One delivery process.",
      h2b: "One ",
      h2hl: "fixed price.",
      h2c: " 90 days.",
      lead: "30 min intro call. We look at one concrete delivery flow. At the end you know if and how it pays off for you.",
      cta: "Book intro call",
      ctaNote: "// No sales pitch. No deck. A look at your delivery.",
      sticky: "Intro call",
    },
  },
};

const AgencyLanding = () => {
  const lang = useLang();
  return <VerticalLanding c={copy[lang]} />;
};

export default AgencyLanding;
